import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { portalApi } from '../services/portalApi';
import './PortalContractDetailPage.css';

function money(v) { return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—' }

export default function PortalContractDetailPage() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();
  const [contract,   setContract]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [dlLoading,  setDlLoading]  = useState(false);
  const [justSigned, setJustSigned] = useState(false);
  const pollRef = useRef(null);

  const returnedFromSigning = new URLSearchParams(location.search).has('signed');

  useEffect(() => {
    portalApi.contract(id)
      .then(data => {
        setContract(data);
        // If the user just came back from /sign/:token, show the confirmation banner.
        // If the signature webhook hasn't fired yet (status still 'Sent'), poll until it does.
        if (returnedFromSigning) {
          setJustSigned(true);
          if (data?.status !== 'Signed') startPolling();
        }
      })
      .catch(() => setError('Contract not found.'))
      .finally(() => setLoading(false));

    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function startPolling() {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 12) { clearInterval(pollRef.current); return; } // stop after ~60s
      try {
        const data = await portalApi.contract(id);
        setContract(data);
        if (data?.status === 'Signed') clearInterval(pollRef.current);
      } catch { clearInterval(pollRef.current); }
    }, 5000);
  }

  async function downloadPdf() {
    setDlLoading(true);
    try {
      const blob = await portalApi.contractPdf(id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${contract.title || 'contract'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('PDF is not available yet. Please try again shortly.');
    } finally {
      setDlLoading(false);
    }
  }

  if (loading) return <div className="pcd-page"><div className="pcd-loading"><div className="pcd-spin" /></div></div>;
  if (error)   return <div className="pcd-page"><div className="pcd-error-msg">{error}</div></div>;

  const isSigned  = contract.status === 'Signed';
  const isPending = contract.status === 'Sent';
  const hasFees   = contract.setupFee > 0 || contract.monthlyFee > 0;

  return (
    <div className="pcd-page">
      <button className="pcd-back" onClick={() => navigate('/portal')}>← Back</button>

      {justSigned && !isSigned && (
        <div className="pcd-signing-banner pcd-banner-pending">
          <span aria-hidden="true">⏳</span>
          Signature received — processing your signed copy. This page will update automatically.
        </div>
      )}

      {justSigned && isSigned && (
        <div className="pcd-signing-banner pcd-banner-done">
          <span aria-hidden="true">✓</span>
          Contract signed successfully. Your signed PDF will be available below shortly.
        </div>
      )}

      <div className="pcd-header">
        <span className={`pcd-status-badge ${isSigned ? 'pcd-badge-signed' : isPending ? 'pcd-badge-pending' : 'pcd-badge-draft'}`}>
          {isSigned ? 'Signed' : isPending ? 'Awaiting signature' : 'Draft'}
        </span>
        <h1 className="pcd-title">{contract.title}</h1>
        <p className="pcd-date">
          {isSigned
            ? `Signed on ${fmtDate(contract.signedAt)}`
            : `Sent for signature on ${fmtDate(contract.createdAt)}`}
        </p>
      </div>

      {hasFees && (
        <div className="pcd-fees">
          {contract.setupFee > 0   && <div className="pcd-fee-item"><span>Setup fee</span><strong>{money(contract.setupFee)}</strong></div>}
          {contract.monthlyFee > 0 && <div className="pcd-fee-item"><span>Monthly retainer</span><strong>{money(contract.monthlyFee)}/mo</strong></div>}
          {contract.startDate      && <div className="pcd-fee-item"><span>Start date</span><strong>{fmtDate(contract.startDate)}</strong></div>}
        </div>
      )}

      <div
        className="pcd-body"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.body, {
          FORBID_TAGS: ['form','input','textarea','select','button','iframe','frame','object','embed','base','script'],
          FORBID_ATTR: ['action','formaction','onerror','onload'],
        }) }}
      />

      <div className="pcd-actions">
        {isPending && (
          <a
            href={`/sign/${contract.signingToken}?returnUrl=/portal/contracts/${id}?signed`}
            className="pcd-btn pcd-btn-primary"
          >
            Review &amp; Sign
          </a>
        )}
        {isSigned && contract.pdfUrl && (
          <button
            className="pcd-btn pcd-btn-secondary"
            onClick={downloadPdf}
            disabled={dlLoading}
          >
            {dlLoading ? 'Preparing…' : '↓ Download signed PDF'}
          </button>
        )}
      </div>
    </div>
  );
}
